import { useState, useRef, useEffect, CSSProperties } from 'react';
import { SlidersHorizontal, ExternalLink } from 'lucide-react';
import { useFeatureFlagOverrides } from '../contexts/FeatureFlagOverrideContext';
import { analytics } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';
import settings from '../config/settings.json';
import { ROLLOUT_FLAGS as FLAGS } from '../config/rolloutFlags';


/**
 * Session-scoped feature flag overrides, plus the parent portal's portal-switch links.
 *
 * `canToggleFlags` is passed in by each mount site rather than read from `useAuth` here: the
 * teacher and admin portals render outside the parent `AuthProvider`, so the roles this
 * component can see there are the context defaults, not the real ones. Deciding at the mount
 * point is both correct and readable — the admin layout says `true`, the teacher layout does
 * not, and there is no way to be wrong by accident.
 *
 * Teachers deliberately cannot switch flags. A staged rollout stops meaning anything if the
 * people it is being staged to can opt themselves in, and a teacher toggling a parent-facing
 * flag mid-shift changes what the office sees without anybody knowing why. Note this is an
 * affordance, not a security boundary — overrides live in `sessionStorage` and flags are not
 * authorization. Anything that must not be reachable is gated by RLS, not by this panel.
 */
export function FlagOverridePanel({ canToggleFlags = false }: { canToggleFlags?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState<CSSProperties>({});
  const { overrides, autoOverrides, setOverride } = useFeatureFlagOverrides();
  const { isAdmin, isStaff } = useAuth();
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const popupWidth = 320;
      const spaceRight = window.innerWidth - rect.left;
      const left = spaceRight >= popupWidth ? rect.left : rect.right - popupWidth;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow >= 200) {
        setPopupStyle({ position: 'fixed', top: rect.bottom + 4, left: Math.max(8, left), width: popupWidth, zIndex: 9999 });
      } else {
        setPopupStyle({ position: 'fixed', bottom: window.innerHeight - rect.top + 4, left: Math.max(8, left), width: popupWidth, zIndex: 9999 });
      }
    }
    setIsOpen(p => !p);
  };

  const knownFlagKeys = FLAGS.map(f => f.flag);
  const activeOverrideCount = canToggleFlags
    ? knownFlagKeys.filter(f => f in overrides).length
    : 0;
  const isOnAdminApp = window.location.pathname.startsWith('/admin-app');
  const isOnStaffApp = window.location.pathname.startsWith('/staff-app');

  // Portal switch links. In the admin and teacher portals these live as
  // first-class main-menu items, so here we only surface them on the parent
  // portal (where AuthContext is available).
  const portalLinks: { label: string; href: string }[] = [];
  if (!isOnAdminApp && !isOnStaffApp) {
    if (isAdmin) portalLinks.push({ label: 'Admin Portal', href: '/admin-app' });
    if (isStaff) portalLinks.push({ label: 'Teacher Portal', href: '/staff-app' });
  }

  // Nothing to toggle and nowhere to switch to: render no control at all rather than a button
  // that opens an empty popup. This is what removes the sliders icon from the teacher portal.
  if (!canToggleFlags && portalLinks.length === 0) return null;

  return (
    <div className="relative" ref={panelRef}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        title={canToggleFlags ? 'Feature flag overrides (admin only)' : 'Switch portal'}
        aria-label="Toggle feature flag panel"
        className={`flex items-center p-2 rounded-lg hover:bg-gray-100 ${activeOverrideCount > 0 ? 'text-amber-600' : 'text-gray-400 hover:text-gray-600'}`}
      >
        <SlidersHorizontal className="h-5 w-5" />
        {activeOverrideCount > 0 && (
          <span className="ml-1 text-xs font-semibold">{activeOverrideCount}</span>
        )}
      </button>

      {isOpen && (
        <div style={popupStyle} className="bg-white/90 backdrop-blur-lg rounded-lg shadow-lg border border-gray-200 p-3">
          {canToggleFlags && activeOverrideCount > 0 && (
            <div className="flex justify-end mb-1">
              <button
                onClick={() => knownFlagKeys.forEach(f => setOverride(f, undefined))}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Reset all
              </button>
            </div>
          )}
          {portalLinks.length > 0 && (
            <div className="pb-2 mb-2 border-b border-gray-100 flex flex-col gap-1.5">
              {portalLinks.map(link => (
                <button
                  key={link.href}
                  onClick={() => { window.location.href = link.href; }}
                  className="flex items-center gap-2 w-full px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 hover:bg-indigo-50 hover:border-indigo-200 text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                  {link.label}
                </button>
              ))}
            </div>
          )}

          {canToggleFlags && FLAGS.map(({ flag, label }) => {
            const canonical = (settings as Record<string, unknown>)[flag] === true;
            const override = overrides[flag];
            return (
              <div key={flag} className="flex items-center justify-between py-2 border-t border-gray-100">
                <div>
                  <p className="text-sm font-medium text-gray-800">{label}</p>
                  <p className="text-xs text-gray-400">
                    settings.json: <span className={canonical ? 'text-green-600' : 'text-gray-400'}>{canonical ? 'ON' : 'OFF'}</span>
                    {autoOverrides[flag] !== undefined && (
                      <span data-testid={`flag-auto-${flag}`}>
                        {' · '}for you: <span className={autoOverrides[flag] ? 'text-green-600' : 'text-gray-400'}>{autoOverrides[flag] ? 'ON' : 'OFF'}</span>
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  {(['default', 'on', 'off'] as const).map(opt => {
                    const isActive =
                      opt === 'default' ? override === undefined :
                      opt === 'on' ? override === true :
                      override === false;
                    return (
                      <button
                        key={opt}
                        onClick={() => {
                          setOverride(flag, opt === 'default' ? undefined : opt === 'on');
                          analytics.trackFeatureFlagOverride({ flag, value: opt, canonicalValue: canonical });
                        }}
                        className={`px-2 py-1 rounded capitalize font-medium transition-colors ${
                          isActive
                            ? opt === 'off' ? 'bg-red-100 text-red-700'
                            : opt === 'on' ? 'bg-green-100 text-green-700'
                            : 'bg-gray-200 text-gray-700'
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
